/**
 * The program's exercise-specific warm-up protocol.
 *
 * Transcribed from the "WARM UP PROTOCOL" section of the source spreadsheets
 * (identical across PPL / Full Body / Upper-Lower): the number of warm-up sets
 * prescribed for an exercise selects a pyramid, and each rung is a percentage
 * of that exercise's planned working weight.
 *
 *   1 set  -> ~60%                for ~6-10 reps
 *   2 sets -> ~50%, ~70%          for ~6-10 then 4-6 reps
 *   3 sets -> ~45%, ~65%, ~85%    for ~6-10, 4-6, then 3-4 reps
 */
export interface WarmupRung {
  /** Fraction of the working weight, e.g. 0.6 for 60%. */
  pct: number;
  /** The rep guidance the program gives for this rung. */
  reps: string;
}

const PYRAMIDS: Record<number, WarmupRung[]> = {
  1: [{ pct: 0.60, reps: '6-10' }],
  2: [
    { pct: 0.50, reps: '6-10' },
    { pct: 0.70, reps: '4-6' },
  ],
  3: [
    { pct: 0.45, reps: '6-10' },
    { pct: 0.65, reps: '4-6' },
    { pct: 0.85, reps: '3-4' },
  ],
};

/**
 * The pyramid for a given number of warm-up sets. Counts beyond the three the
 * program documents are extrapolated by evenly spacing extra rungs below the
 * 3-set pyramid, so an unusual prescription still gets sensible guidance
 * rather than nothing.
 */
export function warmupPyramid(setCount: number): WarmupRung[] {
  if (setCount <= 0) return [];
  const known = PYRAMIDS[setCount];
  if (known) return known;

  const base = PYRAMIDS[3];
  const extra = setCount - 3;
  const step = 0.45 / (extra + 1);
  const lead: WarmupRung[] = [];
  for (let i = 0; i < extra; i++) {
    lead.push({ pct: Math.round((step * (i + 1)) * 100) / 100, reps: '6-10' });
  }
  return [...lead, ...base];
}

/**
 * Suggested warm-up load, rounded to the nearest `increment` (default 5, the
 * usual smallest plate jump). Returns null when there's no working weight to
 * base it on yet.
 */
export function suggestedWarmupWeight(
  workingWeight: number | null | undefined,
  pct: number,
  increment = 5,
): number | null {
  if (workingWeight == null || !Number.isFinite(workingWeight) || workingWeight <= 0) return null;
  const raw = workingWeight * pct;
  const rounded = Math.round(raw / increment) * increment;
  // Never suggest nothing for a real working weight — fall back to one increment.
  return rounded > 0 ? rounded : increment;
}

/** Formats a fraction as a whole-number percentage, e.g. 0.65 -> "65%". */
export const formatPct = (pct: number): string => `${Math.round(pct * 100)}%`;
